/**
 * AudioDebatePlayer — queue-based MP3 audio player for the debate feature.
 *
 * Collects base64-encoded MP3 chunks for each speaker turn, decodes them
 * via an HTML <audio> element (using Blob URLs), and plays them sequentially.
 * This approach works reliably across Desktop Chrome, Mobile Chrome (Android),
 * and Mobile Safari (iOS) — unlike the Web Audio API's decodeAudioData which
 * has known issues with MP3 on Mobile Chrome.
 *
 * Fires callbacks so the UI can show who is currently speaking.
 */

export interface AudioTurn {
  speaker: string;
  speakerLabel: string;
  phase: string;
  chunks: string[]; // base64 MP3 data
  complete: boolean;
}

export type SpeakerCallback = (
  speaker: string | null,
  speakerLabel: string | null,
  phase: string | null,
) => void;

export type PlaybackStateCallback = (
  state: "playing" | "paused" | "idle",
) => void;

export class AudioDebatePlayer {
  private audioElement: HTMLAudioElement | null = null;
  private turnQueue: AudioTurn[] = [];
  private isPlaying = false;
  private stopped = false;
  private paused = false;
  private unlocked = false;
  private onSpeakerChange: SpeakerCallback;
  private onPlaybackStateChange: PlaybackStateCallback | null = null;
  private onTurnComplete: ((turn: AudioTurn) => void) | null = null;
  private visibilityHandler: (() => void) | null = null;
  private currentBlobUrl: string | null = null;

  // ── Replay support ────────────────────────────────────────────────────
  // Every completed turn is archived so we can replay the entire debate.
  private playedTurns: AudioTurn[] = [];
  private currentTurn: AudioTurn | null = null; // the turn currently playing

  constructor(onSpeakerChange: SpeakerCallback) {
    this.onSpeakerChange = onSpeakerChange;
  }

  /**
   * Must be called from a user gesture (tap / click handler) to satisfy
   * autoplay policy on iOS.
   *
   * On iOS **all** browsers use the WebKit engine, which requires `play()`
   * to be called *synchronously* within the user-gesture call-stack.
   * We therefore do the absolute minimum here: create the element and call
   * `play()` on it immediately — no `load()`, no data-URI source.
   * An `<audio>` element with no `src` that has had `play()` called within
   * a gesture is considered "unlocked" by WebKit and will allow subsequent
   * programmatic `play()` calls even outside a gesture.
   */
  init() {
    if (!this.audioElement) {
      this.audioElement = new Audio();
      this.audioElement.preload = "auto";
    }

    // ── Gesture-unlock ──────────────────────────────────────────────────
    // The simplest approach that works on iOS Safari, iOS Chrome, and
    // Android Chrome: call play() synchronously on an empty element.
    // Safari treats this as the user-activation grant. It will throw/reject
    // but the grant is already recorded by the time the rejection fires.
    try {
      const p = this.audioElement.play();
      if (p && typeof p.then === "function") {
        p.then(() => {
          this.unlocked = true;
          this.audioElement?.pause();
        }).catch(() => {
          // Expected — there's no source. The *gesture grant* is still
          // recorded by the browser, which is all we need.
          this.unlocked = true;
        });
      } else {
        this.unlocked = true;
      }
    } catch {
      // Synchronous throw (very old browsers) — still consider unlocked
      // since we called play() inside the gesture.
      this.unlocked = true;
    }

    // Listen for page visibility changes (Safari/iOS backgrounding).
    if (!this.visibilityHandler) {
      this.visibilityHandler = () => {
        if (
          document.visibilityState === "visible" &&
          !this.isPlaying &&
          !this.stopped
        ) {
          // Page came back — try to continue playback
          setTimeout(() => {
            if (!this.isPlaying && !this.stopped) {
              this.tryPlayNext();
            }
          }, 200);
        }
      };
      document.addEventListener("visibilitychange", this.visibilityHandler);
    }
  }

  /**
   * Enqueue a base64 audio chunk for the current or a new speaker turn.
   * If `done` is true, that speaker's turn is complete and can be played.
   */
  enqueueChunk(
    speaker: string,
    speakerLabel: string,
    phase: string,
    audioChunk: string | undefined,
    done: boolean,
  ) {
    // Find or create the turn entry
    let turn = this.turnQueue.find(
      (t) => t.speaker === speaker && t.phase === phase && !t.complete,
    );
    if (!turn) {
      turn = { speaker, speakerLabel, phase, chunks: [], complete: false };
      this.turnQueue.push(turn);
    }

    if (audioChunk) {
      turn.chunks.push(audioChunk);
    }

    if (done) {
      turn.complete = true;
      // Try to play if not already playing
      this.tryPlayNext();
    }
  }

  private async tryPlayNext() {
    if (this.isPlaying || this.stopped || this.paused) return;

    // Find the first complete turn that hasn't been played yet
    const nextTurn = this.turnQueue.find((t) => t.complete);
    if (!nextTurn) return;

    // Remove from queue
    this.turnQueue = this.turnQueue.filter((t) => t !== nextTurn);

    this.isPlaying = true;
    this.currentTurn = nextTurn;
    this.onSpeakerChange(
      nextTurn.speaker,
      nextTurn.speakerLabel,
      nextTurn.phase,
    );
    this.emitPlaybackState("playing");

    try {
      await this.playTurn(nextTurn);
    } catch (err) {
      console.error("Audio playback error:", err);
    }

    // Archive the completed turn for replay
    this.playedTurns.push(nextTurn);
    this.currentTurn = null;

    this.isPlaying = false;
    this.onSpeakerChange(null, null, null);
    this.emitPlaybackState(
      this.turnQueue.some((t) => t.complete) ? "playing" : "idle",
    );

    if (this.onTurnComplete) {
      this.onTurnComplete(nextTurn);
    }

    // Proceed to next turn
    if (!this.stopped && !this.paused) {
      this.tryPlayNext();
    }
  }

  private async playTurn(turn: AudioTurn): Promise<void> {
    if (!this.audioElement || turn.chunks.length === 0) return;

    // Combine all base64 chunks into one Uint8Array
    const binaryChunks = turn.chunks.map((b64) => {
      const binary = atob(b64);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
      }
      return bytes;
    });

    const totalLength = binaryChunks.reduce((sum, b) => sum + b.length, 0);
    const combined = new Uint8Array(totalLength);
    let offset = 0;
    for (const chunk of binaryChunks) {
      combined.set(chunk, offset);
      offset += chunk.length;
    }

    // Create a Blob URL — the <audio> element handles MP3 decoding natively,
    // which is far more reliable on Mobile Chrome than Web Audio API's
    // decodeAudioData.
    const blob = new Blob([combined], { type: "audio/mpeg" });
    const blobUrl = URL.createObjectURL(blob);

    // Revoke previous blob URL to avoid memory leaks
    if (this.currentBlobUrl) {
      URL.revokeObjectURL(this.currentBlobUrl);
    }
    this.currentBlobUrl = blobUrl;

    return new Promise<void>((resolve) => {
      if (this.stopped || !this.audioElement) {
        URL.revokeObjectURL(blobUrl);
        if (this.currentBlobUrl === blobUrl) this.currentBlobUrl = null;
        resolve();
        return;
      }

      const audio = this.audioElement!;

      // Safety timeout: if playback stalls or events never fire,
      // resolve so the queue isn't stuck forever.
      // Estimate duration from MP3 bitrate (~128kbps = ~16KB/s)
      const estimatedDurationMs = (totalLength / 16000) * 1000;
      // Give extra headroom — pausing freezes the timer, so use generous max
      const safetyMs = Math.max(estimatedDurationMs * 3 + 10000, 30000);
      const safetyTimer = setTimeout(() => {
        console.warn("Audio playTurn safety timeout — advancing to next turn");
        finish();
      }, safetyMs);

      const finish = () => {
        clearTimeout(safetyTimer);
        audio.onended = null;
        audio.onerror = null;
        // Don't revoke the blob URL here — the element may still reference it
        // internally. It will be revoked when the next turn starts (above) or
        // when stop() is called.
        resolve();
      };

      audio.onended = () => {
        finish();
      };

      audio.onerror = () => {
        const errorCode = audio.error?.code;
        const errorMsg = audio.error?.message || "unknown";
        console.error(
          `Audio playback error: code=${errorCode}, message=${errorMsg}`,
        );
        // Don't reject — resolve so the queue continues to the next turn
        finish();
      };

      // Set source and explicitly call load() — required by Mobile Safari to
      // properly reset the element when changing src on the same <audio> tag.
      audio.src = blobUrl;
      audio.load();

      // Helper: attempt play, with one automatic retry for autoplay rejection.
      // On iOS, the first play() after setting a new src sometimes rejects
      // even on an unlocked element. A short delay + retry resolves it.
      const attemptPlay = (retriesLeft: number) => {
        const playPromise = audio.play();
        if (playPromise && typeof playPromise.then === "function") {
          playPromise.catch((err: Error) => {
            if (retriesLeft > 0) {
              console.warn(
                `Audio play() rejected (${err.message}), retrying in 250ms…`,
              );
              setTimeout(() => {
                if (this.stopped || !this.audioElement) {
                  finish();
                  return;
                }
                attemptPlay(retriesLeft - 1);
              }, 250);
            } else {
              console.error("Audio play() rejected after retries:", err);
              finish();
            }
          });
        }
      };
      attemptPlay(2);
    });
  }

  private emitPlaybackState(state: "playing" | "paused" | "idle") {
    if (this.onPlaybackStateChange) {
      this.onPlaybackStateChange(state);
    }
  }

  setOnTurnComplete(cb: (turn: AudioTurn) => void) {
    this.onTurnComplete = cb;
  }

  setOnPlaybackStateChange(cb: PlaybackStateCallback) {
    this.onPlaybackStateChange = cb;
  }

  // ── Playback controls ─────────────────────────────────────────────────

  /** Pause the current audio and stop advancing to the next turn. */
  pause() {
    if (this.stopped || this.paused) return;
    this.paused = true;
    if (this.audioElement && this.isPlaying) {
      try {
        this.audioElement.pause();
      } catch {
        /* ignore */
      }
    }
    this.emitPlaybackState("paused");
  }

  /** Resume playback from where we left off. */
  resume() {
    if (this.stopped || !this.paused) return;
    this.paused = false;

    if (this.audioElement && this.isPlaying) {
      // We were mid-turn — resume the <audio> element
      const p = this.audioElement.play();
      if (p && typeof p.then === "function") {
        p.catch((err: Error) => {
          console.error("Audio resume play() rejected:", err);
        });
      }
      this.emitPlaybackState("playing");
    } else {
      // We paused between turns — kick the queue
      this.emitPlaybackState("playing");
      this.tryPlayNext();
    }
  }

  /** Whether the player is currently paused. */
  isPaused() {
    return this.paused;
  }

  /**
   * Replay the entire debate from the beginning.
   * Re-queues all archived turns (already played) plus any pending ones,
   * resets completed-turn tracking, and starts playback.
   */
  replay(resetCompletedTurns: () => void) {
    // Stop current playback gracefully
    this.paused = false;
    if (this.audioElement) {
      try {
        this.audioElement.pause();
        this.audioElement.src = "";
      } catch {
        /* ignore */
      }
    }
    if (this.currentBlobUrl) {
      URL.revokeObjectURL(this.currentBlobUrl);
      this.currentBlobUrl = null;
    }

    // Rebuild the full queue: played + current (if any) + remaining
    const allTurns = [
      ...this.playedTurns,
      ...(this.currentTurn ? [this.currentTurn] : []),
      ...this.turnQueue,
    ];
    this.playedTurns = [];
    this.currentTurn = null;
    this.turnQueue = allTurns;
    this.isPlaying = false;

    // Reset the UI's completed-turn list
    resetCompletedTurns();
    this.onSpeakerChange(null, null, null);

    // Start from the top
    this.emitPlaybackState("playing");
    this.tryPlayNext();
  }

  stop() {
    this.stopped = true;
    this.paused = false;
    this.turnQueue = [];
    this.playedTurns = [];
    this.currentTurn = null;
    this.onSpeakerChange(null, null, null);
    this.emitPlaybackState("idle");
    if (this.audioElement) {
      try {
        this.audioElement.pause();
        this.audioElement.src = "";
      } catch {
        /* already stopped */
      }
      this.audioElement = null;
    }
    if (this.currentBlobUrl) {
      URL.revokeObjectURL(this.currentBlobUrl);
      this.currentBlobUrl = null;
    }
    if (this.visibilityHandler) {
      document.removeEventListener("visibilitychange", this.visibilityHandler);
      this.visibilityHandler = null;
    }
  }

  reset() {
    this.stop();
    this.stopped = false;
    this.paused = false;
    this.isPlaying = false;
    this.turnQueue = [];
    this.playedTurns = [];
    this.currentTurn = null;
  }
}
